import React from "react";

import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    Button,
    CircularProgress,
    Fade,
} from "@mui/material";

import CheckCircleRoundedIcon
    from "@mui/icons-material/CheckCircleRounded";

import ErrorRoundedIcon
    from "@mui/icons-material/ErrorRounded";

import WarningRoundedIcon
    from "@mui/icons-material/WarningRounded";

import CloudUploadRoundedIcon
    from "@mui/icons-material/CloudUploadRounded";

import InfoRoundedIcon
    from "@mui/icons-material/InfoRounded";

import CloseRoundedIcon
    from "@mui/icons-material/CloseRounded";


const StatusModal = ({
    open = false,

    status = "loading",

    title,

    message,

    buttonText = "Continuar",

    onClose,

    showClose = true,
}) => {


    /*
    |--------------------------------------------------------------------------
    | CONFIGURACIÓN SEGÚN ESTADO
    |--------------------------------------------------------------------------
    */

    const config = {

        loading: {
            icon: (
                <Box
                    sx={{
                        position: "relative",
                        width: 100,
                        height: 100,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >

                    <CircularProgress
                        size={78}
                        thickness={4}
                        sx={{
                            color: "#22324A",
                        }}
                    />

                    <CloudUploadRoundedIcon
                        sx={{
                            position: "absolute",
                            fontSize: 34,
                            color: "#22324A",
                        }}
                    />

                </Box>
            ),

            iconBackground: "#EAF0F6",

            defaultTitle: "Procesando...",

            defaultMessage:
                "Estamos procesando tu solicitud. Por favor espera un momento.",

            showButton: false,

        },


        success: {

            icon: (
                <CheckCircleRoundedIcon
                    sx={{
                        fontSize: 68,
                        color: "#2E7D32",
                    }}
                />
            ),

            iconBackground: "#EAF7EE",

            defaultTitle: "¡Operación exitosa!",

            defaultMessage:
                "La operación se realizó correctamente.",

            showButton: true,

        },


        error: {

            icon: (
                <ErrorRoundedIcon
                    sx={{
                        fontSize: 68,
                        color: "#C62828",
                    }}
                />
            ),

            iconBackground: "#FFF0F0",

            defaultTitle: "Ocurrió un problema",

            defaultMessage:
                "No fue posible completar la operación.",

            showButton: true,

        },


        warning: {

            icon: (
                <WarningRoundedIcon
                    sx={{
                        fontSize: 68,
                        color: "#B7791F",
                    }}
                />
            ),

            iconBackground: "#FFF7E6",

            defaultTitle: "Advertencia",

            defaultMessage:
                "Revisa la información antes de continuar.",

            showButton: true,

        },


        info: {

            icon: (
                <InfoRoundedIcon
                    sx={{
                        fontSize: 68,
                        color: "#31516F",
                    }}
                />
            ),

            iconBackground: "#EEF4FA",

            defaultTitle: "Información",

            defaultMessage:
                "Información importante.",

            showButton: true,

        },

    };


    const current =
        config[status] || config.info;


    const isLoading =
        status === "loading";


    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (

        <Dialog

            open={open}

            onClose={
                isLoading
                    ? undefined
                    : onClose
            }

            maxWidth="xs"

            fullWidth

            TransitionComponent={Fade}

            PaperProps={{
                sx: {

                    borderRadius: "24px",

                    overflow: "hidden",

                    boxShadow:
                        "0px 24px 70px rgba(18, 33, 63, 0.18)",

                    maxWidth: "430px",

                },
            }}

        >

            <DialogContent
                sx={{
                    p: 0,
                }}
            >

                {/* =====================================================
                    CONTENIDO
                ====================================================== */}

                <Box
                    sx={{
                        px: 4,
                        pt: 5,
                        pb: 3,
                        textAlign: "center",
                        position: "relative",
                    }}
                >

                    {/* CERRAR */}

                    {!isLoading &&
                        showClose && (

                            <Button

                                onClick={onClose}

                                sx={{
                                    position:
                                        "absolute",

                                    top: 14,

                                    right: 14,

                                    minWidth: 38,

                                    width: 38,

                                    height: 38,

                                    borderRadius:
                                        "50%",

                                    color:
                                        "#7A828D",

                                    p: 0,

                                    "&:hover": {
                                        backgroundColor:
                                            "#F3F5F7",
                                    },
                                }}

                            >

                                <CloseRoundedIcon
                                    fontSize="small"
                                />

                            </Button>

                        )}


                    {/* =================================================
                        ICONO
                    ================================================== */}

                    <Box
                        sx={{
                            width: 110,

                            height: 110,

                            borderRadius:
                                "50%",

                            backgroundColor:
                                current.iconBackground,

                            display: "flex",

                            alignItems:
                                "center",

                            justifyContent:
                                "center",

                            mx: "auto",

                            mb: 3,
                        }}
                    >

                        {current.icon}

                    </Box>


                    {/* =================================================
                        TITULO
                    ================================================== */}

                    <Typography
                        sx={{
                            fontSize:
                                "1.45rem",

                            fontWeight: 700,

                            color:
                                "#22324A",

                            mb: 1,
                        }}
                    >

                        {title ||
                            current.defaultTitle}

                    </Typography>


                    {/* =================================================
                        MENSAJE
                    ================================================== */}

                    <Typography
                        sx={{
                            color:
                                "#7A828D",

                            fontSize:
                                "0.95rem",

                            lineHeight:
                                1.6,

                            maxWidth:
                                "330px",

                            mx: "auto",

                            whiteSpace:
                                "pre-line",
                        }}
                    >

                        {message ||
                            current.defaultMessage}

                    </Typography>

                </Box>


                {/* =====================================================
                    BOTÓN
                ====================================================== */}

                {!isLoading &&
                    current.showButton && (

                        <Box
                            sx={{
                                px: 4,
                                pb: 4,
                            }}
                        >

                            <Button

                                fullWidth

                                variant="contained"

                                onClick={onClose}

                                sx={{
                                    height: 48,

                                    borderRadius:
                                        2.5,

                                    backgroundColor:

                                        status ===
                                        "error"

                                            ? "#C62828"

                                            : "#22324A",

                                    textTransform:
                                        "none",

                                    fontSize:
                                        "0.95rem",

                                    fontWeight:
                                        600,

                                    "&:hover": {

                                        backgroundColor:

                                            status ===
                                            "error"

                                                ? "#A91F1F"

                                                : "#182536",
                                    },
                                }}

                            >

                                {buttonText}

                            </Button>

                        </Box>

                    )}

            </DialogContent>

        </Dialog>
    );
};


export default StatusModal;